import {
  IDBCommunity,
  IPeriods,
  IScheduleTypes,
  USER_ROLES,
  PAYMENT_STATUS,
  PRM_STATUSES,
  REPARTITION_TYPE,
  CONSUMER_INVOICE_STATUSES,
} from 'constants/types';
import { IUserNotificationSettings } from '../constants/types/notification-settings';

export enum COMMUNITY_TYPE {
  S21 = 'S21',
  AO_CRE_AUTOCONSOMMATION = 'AO_CRE_AUTOCONSOMMATION',
  AO_CRE_INJECTION = 'AO_CRE_INJECTION',
}

export type CreateCommunityPayload = {
  name: string;
  address: CommunityAddress;
  pmoOrgName: string;
  sirenNumber: number | null;
  pmo: PMOInfo;
  enedisAgreed: boolean;
  supportAgreed: boolean;
  maxConsumers: number | null;
  maxProducers: number | null;
  maxDistance: number | null;
  maxPower: number | null;
  agreementId: string;
  files?: string[];
  repartitionType: string;
  communityType?: COMMUNITY_TYPE;
  hasWinterDistribution?: boolean;
  repartitionSchedule?: Record<number, boolean>;
};

export type PMOAddress = {
  city: string;
  country: string;
  postalCode: string;
  street: string;
  building: string;
};

type PMOInfo = {
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  contactNumber: number | string | null;
  address: PMOAddress;
};

type CommunityAddress = {
  city: string;
  country: string;
  postalCode: string;
  location: {
    coordinates: number[] | null[];
  };
};

export type CreateConsumerPayload = {
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  contactNumber: number | string | null;
  role: string;
  address: ConsumerAddress;
  community?: string;
  additionalInfo: {
    type: string;
    siretNumber: string | null;
    prms: IPrmNumber[];
    power?: number | null;
    billingAddress: PMOAddress;
    billingPerson: {
      firstName: string;
      middleName: string;
      lastName: string;
      email: string;
      contactNumber: string;
    };
    legalPerson: LegalPerson;
    cppaSigned: boolean;
    agreementSigned: boolean;
    enedisConsentSigned: boolean;
    paymentMethodAdded?: boolean;
    hasSpecialTariffs?: boolean;
    isSkipTaxEnabled?: boolean;
    billingPeriod?: BILLING_PERIOD;
    nextBillingPeriod?: BILLING_PERIOD;
  };
  notificationSettings?: IUserNotificationSettings;
};

interface ConsumerAddress extends PMOAddress {
  location: {
    coordinates: number[];
    type?: string;
  };
}

export interface IRange {
  start: string;
  endDate: string;
}

export interface IElectricityPrice {
  dynamic?: {
    RH?: number | null;
    HS_HH?: number | null;
    HS_LH?: number | null;
    LS_HH?: number | null;
    LS_LH?: number | null;
  };
  static?: number | null;
}

export interface LegalPerson {
  firstName: string;
  lastName: string;
  middleName: string;
  email: string;
  contactNumber: string | number;
  position: string;
}

export enum BILLING_PERIOD {
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  SEMI_ANNUALLY = 'semi-annually',
  ANNUALLY = 'annually',
}

interface IAdditionalInfo {
  siretNumber: string | null;
  power: number | null;
  vatNumber: number | null;
  sharedCapital: string;
  billingAddress: ConsumerAddress;
  billingPerson: {
    firstName: string;
    lastName: string;
    middleName: string;
    email: string;
    contactNumber: string | number;
  };
  legalPerson: LegalPerson;
  prms: IProducerPrmNumber[];
  agreementSigned: boolean;
  licenseOperationSigned: boolean;
  consumptionAgreementSigned: boolean;
  enedisConsentSigned: boolean;
  isSkipTaxEnabled?: boolean;
  billingPeriod?: BILLING_PERIOD;
  nextBillingPeriod?: BILLING_PERIOD;
}

export type CreateProducerPayload = {
  firstName: string;
  lastName: string;
  middleName: string;
  email: string;
  contactNumber: string | number;
  role: string;
  address: ConsumerAddress;
  community?: string;
  additionalInfo: IAdditionalInfo;
};

export interface Suggestion {
  center: number[];
  text: string;
  place_name: string;
  context: any[];
}

export interface IMember extends CreateConsumerPayload {
  createdAt: string;
  updatedAt: string;
  _id?: string;
  auth0Id: string;
  status: string;
}

export interface ILabelMap {
  totalConsumption?: string;
  totalProduction?: string;
  selfConsumption: string;
  gridConsumption?: string;
  overproduction?: string;
}

type IProducerPrmNumber = {
  prmValue: number | null;
};

export enum PRM_TYPE {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
  PRIVATE = 'private',
}

export enum USER_STATUSES {
  ACTIVE = 'Active',
  PENDING = 'Pending',
  CONFLICT = 'Conflict',
  DISABLED = 'Disabled',
  DELETION_INITIATED = 'DeletionInitiated',
  DEACTIVATED = 'Deactivated',
}

export interface IUser {
  position?: string;
  email?: string;
  contactNumber?: string;
  firstName: string;
  middleName?: string;
  lastName: string;
}

export interface IDBUser extends IUser {
  _id?: string;
  auth0Id?: string;
  role: USER_ROLES;
  status?: USER_STATUSES;
  address?: ConsumerAddress;
  additionalInfo?: IAdditionalInfo;
  priority?: number;
}

export interface IPrmNumber {
  prmValue: number | null;
  power: number | null;
  counterType: string | null;
  serialNumber: string | null;
  name: string;
  timePeriods: {
    RH?: [IRange] | null;
    HS_HH?: [IRange] | null;
    HS_LH?: [IRange] | null;
    LS_HH?: [IRange] | null;
    LS_LH?: [IRange] | null;
  };
  gridPrice?: IElectricityPrice;
}
export interface IPrmDataDetails {
  prmId: string;
  serialNumber: [string] | string;
}
export interface IDBPrm extends IPrmNumber {
  _id: string;
  user: IDBUser;
  community: string;
  status: USER_STATUSES;
  prmType?: PRM_TYPE;
  role: [USER_ROLES.CONSUMER, USER_ROLES.PRODUCER];
  deactivatedAt?: string;
  updatedAt?: string;
}

export interface ICommunityData {
  consumption: string;
  selfConsumption: string;
  gridConsumption: string;
  overproduction: string;
  consumersAmount: number;
  producersAmount: number;
  maxPover: number;
}
export interface IPayment {
  sessionUrl: string;
}

export interface IPaymentMethod {
  id: string;
  bicAccount: number;
  branchCode: number;
  country: string;
  ibanLast4: number;
  mandateReferenceNumber: string;
  default: boolean;
}

export interface DateRange {
  startDate?: string;
  endDate?: string;
}

export interface DateRangeWithUserId extends DateRange {
  userId?: string;
}

export interface DateRangeWithUserIdAndPrmId extends DateRangeWithUserId {
  prmId?: string | number | undefined;
}

export interface DateRangeWithCommunityId extends DateRange {
  communityId: string | undefined;
}

export interface DateRangeWithCommunityOrUser extends DateRange {
  userId?: string;
  communityId?: string;
}

export interface IDBPrmPrice {
  community: IDBCommunity;
  prm: IDBPrm;
  indexation: number;
  indexType: number;
  cppa: string;
  periods: [IPeriods];
}

export interface IAddPrmPrice {
  indexation: number;
  cppa: string;
  startDate: string;
  endDate: string;
  scheduleTypes: IScheduleTypes;
}

export enum INDEXATION {
  NO = 'no',
  YEARLY = 'yearly',
}

export interface IPrmPaymentStatusMapped {
  paymentStatus: PAYMENT_STATUS;
  prmStatus: PRM_STATUSES;
  prmValue: string;
  prmDeactivatedAt?: string;
  statusErrorMessage?: string;
}

export interface CommunitySettingsPayload {
  hasWinterDistribution?: boolean;
  repartitionSchedule?: Record<number, boolean>;
  maxConsumers: number;
  maxProducers: number;
  maxDistance: number;
  maxPower: number;
  repartitionType: keyof typeof REPARTITION_TYPE;
}

export interface FullCommunitySettingsPayload extends CommunitySettingsPayload {
  address: CommunityAddress;
  name: string;
  agreementId: string;
}

export type EditUserPriorityPayload = {
  users: Array<{ userId?: string; priority?: number }>;
};

export type SgeTierResearchPayload = {
  streetNameAndNumber?: string; // Street number and name (e.g., "8 rue DE L INDUSTRIE"). Optional but recommended if used with `codePostal` and `codeInseeCommune` for precise results.
  postalCode?: string; // Postal code of the address. Optional but required if `codeInseeCommune` is provided. Used together to specify the address.
  communeInseeCode?: string; // INSEE code of the commune. Optional but often used in combination with `codePostal` to identify the location.
  customerNameOrSocialDenomination?: string; // Customer name or social denomination. Required if `rechercheHorsPerimetre` is set to `true`.
  searchOutsidePerimeter?: boolean; // Enables searches outside the supplier's perimeter. Requires `nomClientFinalOuDenominationSociale` and exact or partial name matches (min 3 characters).
  supplyVoltageDomainCode?: string; // Electricity domain code for the supply (e.g., HTB, BT). Provides additional filtering.
  customerCategoryCode?: string; // Final customer category code (e.g., "RES" for residential). Used for filtering by customer type.
  staircaseAndFloorAndApartment?: string; // Apartment details, such as floor, staircase, and apartment number. Typically used with `numeroEtNomVoie`.
  building?: string; // Details of the building (e.g., "Building B"). Optional for refining the address.
  placeName?: string; // Locality information (Lieu-dit). Optional but useful with `numeroEtNomVoie` for precise address identification.
  siretNumber?: string; // SIRET number of the company (e.g., "12345678900013").
  meterSerialNumber?: string; // Meter matricule (C5) or serial number (C2-C4).
};

export type IInvoice = {
  month: number;
  role: USER_ROLES;
  status: CONSUMER_INVOICE_STATUSES;
  total: number;
  year: number;
};
